import React, { useState, useEffect } from "react";
import { ComposableMap, Geography, Geographies } from "react-simple-maps";
import { feature } from "topojson-client";

// GeoJSON URL for US states
const geoUrl = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

// Mapping from state FIPS code to name/symbol
const stateSymbols = {
  "01": { name: "Alabama", symbol: "AL" },
  "02": { name: "Alaska", symbol: "AK" },
  "04": { name: "Arizona", symbol: "AZ" },
  "05": { name: "Arkansas", symbol: "AR" },
  "06": { name: "California", symbol: "CA" },
  "08": { name: "Colorado", symbol: "CO" },
  "09": { name: "Connecticut", symbol: "CT" },
  10: { name: "Delaware", symbol: "DE" },
  11: { name: "District of Columbia", symbol: "DC" },
  12: { name: "Florida", symbol: "FL" },
  13: { name: "Georgia", symbol: "GA" },
  15: { name: "Hawaii", symbol: "HI" },
  16: { name: "Idaho", symbol: "ID" },
  17: { name: "Illinois", symbol: "IL" },
  18: { name: "Indiana", symbol: "IN" },
  19: { name: "Iowa", symbol: "IA" },
  20: { name: "Kansas", symbol: "KS" },
  21: { name: "Kentucky", symbol: "KY" },
  22: { name: "Louisiana", symbol: "LA" },
  23: { name: "Maine", symbol: "ME" },
  24: { name: "Maryland", symbol: "MD" },
  25: { name: "Massachusetts", symbol: "MA" },
  26: { name: "Michigan", symbol: "MI" },
  27: { name: "Minnesota", symbol: "MN" },
  28: { name: "Mississippi", symbol: "MS" },
  29: { name: "Missouri", symbol: "MO" },
  30: { name: "Montana", symbol: "MT" },
  31: { name: "Nebraska", symbol: "NE" },
  32: { name: "Nevada", symbol: "NV" },
  33: { name: "New Hampshire", symbol: "NH" },
  34: { name: "New Jersey", symbol: "NJ" },
  35: { name: "New Mexico", symbol: "NM" },
  36: { name: "New York", symbol: "NY" },
  37: { name: "North Carolina", symbol: "NC" },
  38: { name: "North Dakota", symbol: "ND" },
  39: { name: "Ohio", symbol: "OH" },
  40: { name: "Oklahoma", symbol: "OK" },
  41: { name: "Oregon", symbol: "OR" },
  42: { name: "Pennsylvania", symbol: "PA" },
  44: { name: "Rhode Island", symbol: "RI" },
  45: { name: "South Carolina", symbol: "SC" },
  46: { name: "South Dakota", symbol: "SD" },
  47: { name: "Tennessee", symbol: "TN" },
  48: { name: "Texas", symbol: "TX" },
  49: { name: "Utah", symbol: "UT" },
  50: { name: "Vermont", symbol: "VT" },
  51: { name: "Virginia", symbol: "VA" },
  53: { name: "Washington", symbol: "WA" },
  54: { name: "West Virginia", symbol: "WV" },
  55: { name: "Wisconsin", symbol: "WI" },
  56: { name: "Wyoming", symbol: "WY" },
};

// Component to render the selected state map, centered and scaled
const CenteredStateMap = ({ selectedStateId }) => {
  const [stateFeature, setStateFeature] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      const res = await fetch(geoUrl);
      const topoData = await res.json();
      const states = feature(topoData, topoData.objects.states).features;
      const selected = states.find((state) => state.id === selectedStateId);
      setStateFeature(selected);
    };

    fetchData();
  }, [selectedStateId]);

  return (
    <div style={{ width: 70, height: 70 }}>
      <ComposableMap
        projection="geoMercator"
        width={400}
        height={400}
        projectionConfig={{ scale: 2000 }}
      >
        {stateFeature && (
          <Geographies
            geography={{ type: "FeatureCollection", features: [stateFeature] }}
          >
            {({ geographies, path }) =>
              geographies.map((geo) => {
                const bounds = path.bounds(geo);
                const dx = bounds[1][0] - bounds[0][0];
                const dy = bounds[1][1] - bounds[0][1];
                const x = (bounds[0][0] + bounds[1][0]) / 2;
                const y = (bounds[0][1] + bounds[1][1]) / 2;
                const scale = 180 / Math.max(dx, dy);
                const translate = [200 - scale * x, 200 - scale * y];
                const transform = `translate(${translate[0]}, ${translate[1]}) scale(${scale})`;

                return (
                  <g key={geo.rsmKey} transform={transform}>
                    <Geography geography={geo} fill="#FFF" stroke="#333" />
                  </g>
                );
              })
            }
          </Geographies>
        )}
      </ComposableMap>
    </div>
  );
};

// Main Component
const State = () => {
  const [selected, setSelected] = useState("06"); // Default to California

  // Define handleStateChange inside your component
  const handleStateChange = (fips) => {
    // Add any additional logic here if needed
    console.log("State changed to:", fips);
  };

  return (
    <div
      style={{
        textAlign: "center",
        backgroundColor: "#12273b",
        padding: "0px 20px",
        color: "white",
        borderRadius: "7px",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "10px !important",
          height: "72px",
        }}
      >
        {/* Label and Dropdown in Column */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
          }}
        >
          <div className="dropdown">
            <a
              className="btn btn-secondary  p-2"
              href="#"
              id="dropdown-basic1"
              role="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
              style={{
                width: "250px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              {stateSymbols[selected]?.name
                ? `${stateSymbols[selected].name} (${stateSymbols[selected].symbol})`
                : "Select State"}
            </a>

            <ul
              className="dropdown-menu"
              style={{
                maxHeight: "140px",
                overflowY: "auto",
                width: "330px",
                marginTop: "8px", // Adds spacing between button and dropdown
              }}
            >
              {Object.entries(stateSymbols).map(([fips, { name, symbol }]) => (
                <li key={fips}>
                  <a
                    className="dropdown-item"
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setSelected(fips);
                      handleStateChange(fips);
                    }}
                  >
                    {name} ({symbol})
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
        {/* Enlarged Map */}
        <CenteredStateMap selectedStateId={selected} />
      </div>
    </div>
  );
};

export default State;
